import { SURVEY_QUESTION_TYPE, SURVEY_YES_NO_ANSWER } from '../../domain/domainCodes.js'
import { useEffect, useState } from 'react'
import { BarChart3, Download, MessageSquare } from 'lucide-react'
import Button from '../../components/Button'
import Drawer from '../../components/Drawer'
import Meter from '../../components/Meter'
import { EmptyState, ErrorState, LoadingState } from '../../components/States'
import { downloadSurveyReport, getSurveyReport } from './engagementService'
import { useToast } from '../../components/toastContext'
import { surveysStrings as S } from './strings'
import './SurveyReportDrawer.css'
import { serverDetail } from '../../lib/i18n'

const R = S.report

/** `SurveyQuestionType` — the two free-text kinds have no options to aggregate. */
const SHORT_TEXT = SURVEY_QUESTION_TYPE.SHORT_TEXT
const LONG_TEXT = SURVEY_QUESTION_TYPE.LONG_TEXT
const isText = (type) => type === SHORT_TEXT || type === LONG_TEXT
const answerLabel = (question, answer) => {
  if (question.type !== SURVEY_QUESTION_TYPE.YES_NO) return answer.answer
  return String(answer.answer) === String(SURVEY_YES_NO_ANSWER.YES) ? R.yes : R.no
}

/**
 * تفاصيل الاستبيان — what a survey was answered.
 *
 * `GetSurveyReport` has been in the service since the slice was wired, reachable from
 * nothing, and it was written as an action: it threw the report away and kept the status.
 * It returns each question with its answers counted, plus how many people answered at all.
 *
 * A CHOICE question is a distribution, so each answer is drawn against the largest of its
 * siblings — the shape of the answers is the thing being read, and a column of bare numbers
 * hides it. A FREE-TEXT question has no options: every row is one person's sentence, and the
 * count beside it is 1. Those are listed, not measured.
 */
export default function SurveyReportDrawer({ open, survey, onClose }) {
  const toast = useToast()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  const surveyId = survey?.id

  useEffect(() => {
    if (!open || !surveyId) return
    let live = true
    setLoading(true)
    getSurveyReport(surveyId).then((result) => {
      if (!live) return
      setReport(result)
      setLoading(false)
    })
    return () => {
      live = false
    }
  }, [open, surveyId])

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={R.title}
      icon={BarChart3}
      subtitle={survey?.title}
      onClose={onClose}
      footer={
        /* The endpoint takes a survey id and nothing else — one CSV, no format choice — so
           this is a button rather than the `ExportMenu` the policies drawer wears. */
        <Button
          variant="soft"
          disabled={downloading || loading}
          onClick={async () => {
            setDownloading(true)
            const outcome = await downloadSurveyReport(surveyId)
            setDownloading(false)
            if (!outcome.ok) toast.error(serverDetail(outcome.message) || R.downloadFailed)
          }}
        >
          <Download size={16} strokeWidth={1.75} />
          {R.download}
        </Button>
      }
    >
      {loading ? (
        <LoadingState inline />
      ) : !report?.ok ? (
        <ErrorState title={report?.message || S.actionFailed} />
      ) : report.questions.length === 0 ? (
        <EmptyState icon={MessageSquare} title={R.empty} description={R.emptyHint} />
      ) : (
        <div className="srep">
          <p className="srep__total">{R.total(report.total)}</p>

          {report.questions.map((question, index) => {
            // The scale for the bars: the most-chosen answer, never zero.
            const top = Math.max(1, ...question.answers.map((answer) => answer.count))

            return (
              <section className="srep__question" key={index}>
                <h3 className="srep__title">
                  <span className="srep__index">{index + 1}</span>
                  <bdi>{question.question}</bdi>
                </h3>

                {question.answers.length === 0 ? (
                  <p className="srep__none">{R.noAnswers}</p>
                ) : isText(question.type) ? (
                  <ul className="srep__texts">
                    {question.answers.map((answer, key) => (
                      <li className="srep__text" key={key}>
                        <bdi>{answer.answer}</bdi>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="srep__answers">
                    {question.answers.map((answer, key) => (
                      <li className="srep__answer" key={key}>
                        <span className="srep__label">
                          <bdi>{answerLabel(question, answer)}</bdi>
                          <span className="srep__count">{answer.count.toLocaleString('en-US')}</span>
                        </span>
                        {/* Meter takes a PERCENTAGE, and the scale is the biggest sibling.
                            An answer nobody chose passes `null` rather than 0: Meter draws a
                            1.5% sliver on any non-empty value, so a survey with no answers
                            at all would show a mark on every row. */}
                        <Meter
                          value={answer.count === 0 ? null : (answer.count / top) * 100}
                          label={`${answerLabel(question, answer)}: ${answer.count}`}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </Drawer>
  )
}
